import { useEffect, useState, type FormEvent } from "react";
import { supabase } from "../../lib/supabase.js";
import { useAuth } from "../../lib/auth.js";
import { Button, Field, Input } from "../../lib/ui.js";
import { SectionCard, errorStyle } from "./common.js";

/** Plain-language text for the receptionist-name errors raised by the database (migration 0023). */
export function receptionistNameError(message: string): string | null {
  const staff = message.match(/staff_name_conflict: (.+)/)?.[1];
  if (staff) return `That name is too close to your staff member "${staff.trim()}". Pick a name none of your staff use, so callers can tell them apart.`;
  if (message.includes("invalid_name")) return "Use 2 to 30 letters (spaces, hyphens and apostrophes are fine).";
  return null;
}

/** The name callers hear when the receptionist answers. It can't share a word with any active staff member's name. */
export function ReceptionistSection() {
  const { activeBusinessId } = useAuth();
  const [name, setName] = useState("");
  const [saved, setSaved] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!activeBusinessId) return;
    void supabase.rpc("rpc_portal_get_agent_name", { p_business_id: activeBusinessId }).then(({ data }) => {
      setName((data as string | null) ?? "");
    });
  }, [activeBusinessId]);

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    if (!activeBusinessId) return;
    setError(null);
    setSaved(null);
    setBusy(true);
    const { data, error: err } = await supabase.rpc("rpc_portal_set_agent_name", { p_business_id: activeBusinessId, p_name: name });
    setBusy(false);
    if (err) {
      setError(receptionistNameError(err.message) ?? err.message);
      return;
    }
    setName(data as string);
    setSaved(data as string);
  }

  return (
    <SectionCard
      title="Receptionist"
      description="The name your phone receptionist uses when it answers. It can't be the same as a staff member's name, so callers never confuse the two. Calls that start after you save use the new name."
    >
      <form onSubmit={handleSave} style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
        <Field label="Receptionist's name">
          <Input required value={name} maxLength={30} onChange={(e) => { setSaved(null); setName(e.target.value); }} style={{ width: 220 }} />
        </Field>
        <Button type="submit" variant="primary" disabled={busy || name.trim().length < 2}>
          {busy ? "Saving..." : "Save name"}
        </Button>
        {saved && <span style={{ fontSize: 12.5, color: "var(--rc-success)", paddingBottom: 8 }}>Saved: callers will hear "{saved}".</span>}
      </form>
      {error && <p style={errorStyle}>{error}</p>}
    </SectionCard>
  );
}
